import { useMemo } from 'react';

import { useAuth } from '@/domains/auth';
import { useCriteria } from '@/domains/criteria';
import { useEvent } from '@/domains/events';

import { evidenceSourceMatchesDocumentUrl } from './evidenceSourceMatch';
import { useCompletedExtraction, useValidationReport } from './queries';
import type { EvidenceItem } from './types';

export type DocumentEvidenceItem = EvidenceItem & {
  criterionId: string;
  criterionName: string;
};

export type DocumentEvidenceResult = {
  items: DocumentEvidenceItem[];
  isPending: boolean;
  hasContext: boolean;
};

export function useDocumentEvidence(
  eventId: string,
  docUrl: string | null | undefined,
): DocumentEvidenceResult {
  const { user } = useAuth();
  const perspectiveId = user?.activePerspective?.id ?? null;

  const { data: event } = useEvent(eventId);
  const { data: criteriaData } = useCriteria(event?.applicationId ?? '', {
    perspectiveId: perspectiveId ?? undefined,
  });
  const { data: extractionData, isPending: isExtractionPending } = useCompletedExtraction(eventId);

  const extractionId = extractionData?.items[0]?.id ?? null;
  const criterionIds = useMemo(
    () => (criteriaData?.items ?? []).map((c) => c.id),
    [criteriaData],
  );

  const validation = useValidationReport(extractionId, criterionIds);

  const items = useMemo<DocumentEvidenceItem[]>(() => {
    if (!validation.data || !docUrl) return [];
    const seen = new Set<string>();
    const result: DocumentEvidenceItem[] = [];
    for (const item of validation.data.items) {
      for (const answer of item.answers) {
        for (const ev of answer.evidence) {
          if (!evidenceSourceMatchesDocumentUrl(ev.source, docUrl)) continue;
          // Key op criterium + passage: dezelfde zin onder twee criteria blijft twee rijen.
          const key = `${item.id}|${ev.pages?.join(',') ?? ''}|${ev.sentence ?? ''}`;
          if (seen.has(key)) continue;
          seen.add(key);
          result.push({ ...ev, criterionId: item.id, criterionName: item.name });
        }
      }
    }
    return result;
  }, [validation.data, docUrl]);

  const isReady = extractionId !== null && criterionIds.length > 0;

  return {
    items,
    isPending: isExtractionPending || (isReady && validation.isPending),
    hasContext: isReady,
  };
}
